import type { Category } from '../types/category.types';

/** Maps catalog IDs/keys → display names. Request payloads still use keys/IDs. */
export interface CategoryLabelMaps {
  mainById: Record<string, string>;
  subByKey: Record<string, string>;
  typeByKey: Record<string, string>;
}

/** Match backend generateKeyFromName (fallback when API omits `key`). */
export const generateKeyFromName = (name: string): string =>
  name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

export function buildCategoryLabelMaps(categories: Category[]): CategoryLabelMaps {
  const mainById: Record<string, string> = {};
  const subByKey: Record<string, string> = {};
  const typeByKey: Record<string, string> = {};

  for (const category of categories) {
    mainById[category.categoryId] = category.name;
    mainById[category.name] = category.name;

    for (const sub of category.subCategories || []) {
      const subKey = sub.key || generateKeyFromName(sub.name);
      subByKey[subKey] = sub.name;
      subByKey[sub.name] = sub.name;

      for (const type of sub.types || []) {
        const typeKey = type.key || generateKeyFromName(type.name);
        typeByKey[typeKey] = type.name;
        typeByKey[type.name] = type.name;
      }
    }
  }

  return { mainById, subByKey, typeByKey };
}

export function formatCategoryLabel(
  value: string | undefined | null,
  map: Record<string, string>,
): string {
  if (!value) return '';
  return map[value] || value;
}

export function formatCategoryLabels(
  values: string[] | undefined | null,
  map: Record<string, string>,
): string {
  if (!values || values.length === 0) return '';
  return values.map((v) => map[v] || v).join(', ');
}
